import React, { createContext, useState, useEffect, useContext } from 'react';
import Web3 from 'web3';
import P2PTradeABI from '../contracts/P2PTradeABI.json';

const Web3Context = createContext();
// Enforce BSC Mainnet (Chain ID: 56) strictly
const SKIP_NETWORK_CHECK = false; // Must be false to enforce BSC Mainnet only

// Contract addresses - Deployed P2PTrade Contract Address
const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS || '0x28124CaeF97Bc376998dD5e7CCc3B86d41e1008A'; // Deployed P2PTrade contract
const DEFAULT_USDT_ADDRESS = '0x55d398326f99059fF775485246999027B3197955'; // BSC Mainnet USDT fallback
const DEFAULT_TOKEN_ADDRESS = '0xEef87510Acee3CBb63B7C4bdDF9e58B66B8Db757'; // Main token address fallback

// BSC RPC Endpoints
const BSC_RPC_URLS = [
  'https://bsc-dataseed.binance.org/',
  'https://bsc-dataseed1.binance.org/',
  'https://bsc-dataseed2.binance.org/',
  'https://bsc-dataseed3.binance.org/',
  'https://bsc-dataseed4.binance.org/'
];

// Standard ERC20 ABI for token interactions
const ERC20_ABI = [
  {
    "constant": true,
    "inputs": [{ "name": "_owner", "type": "address" }],
    "name": "balanceOf",
    "outputs": [{ "name": "balance", "type": "uint256" }],
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      { "name": "_spender", "type": "address" },
      { "name": "_value", "type": "uint256" }
    ],
    "name": "approve",
    "outputs": [{ "name": "", "type": "bool" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [
      { "name": "_owner", "type": "address" },
      { "name": "_spender", "type": "address" }
    ],
    "name": "allowance",
    "outputs": [{ "name": "", "type": "uint256" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "decimals",
    "outputs": [{ "name": "", "type": "uint8" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "symbol",
    "outputs": [{ "name": "", "type": "string" }],
    "type": "function"
  }
];

export const Web3Provider = ({ children }) => {
  const [web3, setWeb3] = useState(null);
  const [account, setAccount] = useState(null);
  const [contract, setContract] = useState(null);
  const [usdtContract, setUsdtContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [usdtAddress, setUsdtAddress] = useState(DEFAULT_USDT_ADDRESS);
  const [tokenAddress, setTokenAddress] = useState(DEFAULT_TOKEN_ADDRESS);
  const [connectionType, setConnectionType] = useState(null); // 'metamask' or 'privatekey'
  const [privateKeyAccount, setPrivateKeyAccount] = useState(null);

  // Switch wallet network to BSC Mainnet (Chain ID 56)
  const switchToBSCMainnet = async () => {
    if (!window.ethereum) return false;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x38' }],
      });
      setError(null);
      return true;
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: '0x38',
              chainName: 'Binance Smart Chain Mainnet',
              nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
              rpcUrls: ['https://bsc-dataseed.binance.org/'],
              blockExplorerUrls: ['https://bscscan.com/']
            }],
          });
          setError(null);
          return true;
        } catch (addError) {
          console.error('Error adding BSC network:', addError);
        }
      } else {
        console.error('Error switching to BSC network:', switchError);
      }
    }
    return false;
  };

  // Initialize Web3 with MetaMask
  const initWeb3WithMetaMask = async () => {
    try {
      if (window.ethereum) {
        const web3Instance = new Web3(window.ethereum);
        setWeb3(web3Instance);
        setConnectionType('metamask');

        // Get network ID
        const networkId = await web3Instance.eth.getChainId();
        setChainId(networkId);

        // Check if on BSC Mainnet (chainId: 56)
        if (!SKIP_NETWORK_CHECK && networkId !== 56) {
          setError('Please connect your wallet to Binance Smart Chain Mainnet (Chain ID: 56)');
          await switchToBSCMainnet();
          return;
        }

        // Initialize contracts
        const contractInstance = await initializeContracts(web3Instance);

        // Check if already connected
        const accounts = await web3Instance.eth.getAccounts();
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          await checkUserRole(contractInstance, accounts[0]);
        }

        // Set up event listeners
        window.ethereum.on('accountsChanged', handleAccountsChanged);
        window.ethereum.on('chainChanged', handleChainChanged);

        setLoading(false);
      } else {
        setError('Please install MetaMask to use this application');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error initializing Web3:', err);
      setError('Failed to initialize Web3: ' + err.message);
      setLoading(false);
    }
  };

  // Initialize Web3 with Private Key
  const initWeb3WithPrivateKey = async (privateKey) => {
    try {
      // Validate private key format
      if (!privateKey.startsWith('0x')) {
        privateKey = '0x' + privateKey;
      }

      if (!/^0x[0-9a-fA-F]{64}$/.test(privateKey)) {
        throw new Error('Invalid private key format');
      }

      // Create Web3 instance with BSC RPC
      const web3Instance = new Web3(new Web3.providers.HttpProvider(BSC_RPC_URLS[0]));

      // Add account from private key
      const account = web3Instance.eth.accounts.privateKeyToAccount(privateKey);
      web3Instance.eth.accounts.wallet.add(account);
      web3Instance.eth.defaultAccount = account.address;

      setWeb3(web3Instance);
      setAccount(account.address);
      setPrivateKeyAccount(account);
      setConnectionType('privatekey');

      // Get network ID
      const networkId = await web3Instance.eth.getChainId();
      setChainId(networkId);

      // Check if on BSC Mainnet (chainId: 56) - Skip in development mode
      if (!SKIP_NETWORK_CHECK && networkId !== 56) {
        setError('Connected to wrong network. Please ensure you are using BSC Mainnet RPC');
        setLoading(false);
        return;
      }

      // Initialize contracts
      const contractInstance = await initializeContracts(web3Instance);
      await checkUserRole(contractInstance, account.address);

      setLoading(false);
      setError(null);

      // Store encrypted private key in sessionStorage for session persistence
      // Note: In production, consider more secure storage methods
      sessionStorage.setItem('encryptedKey', btoa(privateKey));

      return true;
    } catch (err) {
      console.error('Error initializing Web3 with private key:', err);
      setError('Failed to initialize with private key: ' + err.message);
      setLoading(false);
      return false;
    }
  };

  // Initialize contracts
  const initializeContracts = async (web3Instance) => {
    try {
      const abiToUse = P2PTradeABI.abi || P2PTradeABI;
      const contractInstance = new web3Instance.eth.Contract(
        abiToUse,
        CONTRACT_ADDRESS
      );
      setContract(contractInstance);

      let activeUsdtAddr = DEFAULT_USDT_ADDRESS;
      let activeTokenAddr = DEFAULT_TOKEN_ADDRESS;

      try {
        const [onChainUsdt, onChainToken] = await Promise.all([
          contractInstance.methods.usdt().call(),
          contractInstance.methods.token().call()
        ]);
        if (web3Instance.utils.isAddress(onChainUsdt) && onChainUsdt !== '0x0000000000000000000000000000000000000000') {
          activeUsdtAddr = onChainUsdt;
        }
        if (web3Instance.utils.isAddress(onChainToken) && onChainToken !== '0x0000000000000000000000000000000000000000') {
          activeTokenAddr = onChainToken;
        }
      } catch (e) {
        console.warn('Using default fallback ERC20 token addresses:', e);
      }

      setUsdtAddress(activeUsdtAddr);
      setTokenAddress(activeTokenAddr);

      const usdtInstance = new web3Instance.eth.Contract(
        ERC20_ABI,
        activeUsdtAddr
      );
      setUsdtContract(usdtInstance);

      const tokenInstance = new web3Instance.eth.Contract(
        ERC20_ABI,
        activeTokenAddr
      );
      setTokenContract(tokenInstance);

      return contractInstance; // Return the contract instance for immediate use
    } catch (err) {
      console.error('Error initializing contracts:', err);
      throw err;
    }
  };

  // Check if user is owner or admin
  const checkUserRole = async (contractInstance, userAccount) => {
    try {
      if (!contractInstance || !userAccount) return;

      const owner = await contractInstance.methods.owner().call();
      setIsOwner(owner.toLowerCase() === userAccount.toLowerCase());

      const adminStatus = await contractInstance.methods.admins(userAccount).call();
      setIsAdmin(adminStatus || owner.toLowerCase() === userAccount.toLowerCase());
    } catch (err) {
      console.error('Error checking user role:', err);
    }
  };

  // Handle account changes (MetaMask only)
  const handleAccountsChanged = async (accounts) => {
    if (connectionType === 'metamask') {
      if (accounts.length === 0) {
        setAccount(null);
        setIsOwner(false);
        setIsAdmin(false);
      } else {
        setAccount(accounts[0]);
        if (contract) {
          await checkUserRole(contract, accounts[0]);
        }
      }
    }
  };

  // Handle chain changes (MetaMask only)
  const handleChainChanged = (chainId) => {
    if (connectionType === 'metamask') {
      // Re-initialize Web3 when chain changes instead of reloading
      initWeb3WithMetaMask();
    }
  };

  // Connect wallet with MetaMask
  const connectWallet = async () => {
    try {
      if (!window.ethereum) {
        setError('Please install MetaMask');
        return;
      }

      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      setAccount(accounts[0]);
      setConnectionType('metamask');
      setPrivateKeyAccount(null);

      // Initialize Web3 with MetaMask if not already initialized
      if (!web3 || connectionType !== 'metamask') {
        const web3Instance = new Web3(window.ethereum);
        setWeb3(web3Instance);
        const contractInstance = await initializeContracts(web3Instance);
        await checkUserRole(contractInstance, accounts[0]);
      } else if (contract) {
        await checkUserRole(contract, accounts[0]);
      }

      sessionStorage.removeItem('encryptedKey');
      setError(null);
    } catch (err) {
      console.error('Error connecting wallet:', err);
      setError('Failed to connect wallet: ' + err.message);
    }
  };

  // Connect with private key
  const connectWithPrivateKey = async (privateKey) => {
    try {
      setLoading(true);
      const success = await initWeb3WithPrivateKey(privateKey);
      return success;
    } catch (err) {
      console.error('Error connecting with private key:', err);
      setError('Failed to connect with private key: ' + err.message);
      return false;
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    setAccount(null);
    setIsOwner(false);
    setIsAdmin(false);
    setConnectionType(null);
    setPrivateKeyAccount(null);
    sessionStorage.removeItem('encryptedKey');

    // Clear wallet if using private key
    if (web3 && web3.eth.accounts.wallet) {
      web3.eth.accounts.wallet.clear();
    }
  };

  // Get token balances
  const getBalances = async () => {
    if (!account || !web3 || !usdtContract || !tokenContract) return {};

    try {
      const [bnbBalance, usdtBalance, tokenBalance] = await Promise.all([
        web3.eth.getBalance(account),
        usdtContract.methods.balanceOf(account).call(),
        tokenContract.methods.balanceOf(account).call()
      ]);

      return {
        bnb: web3.utils.fromWei(bnbBalance, 'ether'),
        usdt: web3.utils.fromWei(usdtBalance, 'ether'),
        token: web3.utils.fromWei(tokenBalance, 'ether')
      };
    } catch (err) {
      console.error('Error getting balances:', err);
      return { bnb: '0', usdt: '0', token: '0' };
    }
  };

  // Check and approve token allowance
  const checkAndApproveToken = async (tokenAddress, spenderAddress, amount) => {
    if (!account || !web3) return false;

    try {
      const tokenInstance = new web3.eth.Contract(ERC20_ABI, tokenAddress);
      const currentAllowance = await tokenInstance.methods.allowance(account, spenderAddress).call();

      if (window.BigInt(currentAllowance) < window.BigInt(amount)) {
        let tx;

        if (connectionType === 'privatekey' && privateKeyAccount) {
          // Sign and send transaction with private key
          const txData = tokenInstance.methods.approve(spenderAddress, amount).encodeABI();
          const gasPrice = await web3.eth.getGasPrice();
          const nonce = await web3.eth.getTransactionCount(account);

          const txObject = {
            from: account,
            to: tokenAddress,
            data: txData,
            gas: await tokenInstance.methods.approve(spenderAddress, amount).estimateGas({ from: account }),
            gasPrice: gasPrice,
            nonce: nonce
          };

          const signedTx = await web3.eth.accounts.signTransaction(txObject, privateKeyAccount.privateKey);
          const receipt = await web3.eth.sendSignedTransaction(signedTx.rawTransaction);
          return receipt.status;
        } else {
          // Use MetaMask
          tx = await tokenInstance.methods.approve(spenderAddress, amount).send({ from: account });
          return tx.status;
        }
      }
      return true;
    } catch (err) {
      console.error('Error approving token:', err);
      throw err;
    }
  };

  // Send transaction helper for private key connections
  const sendTransaction = async (transactionObject) => {
    try {
      if (connectionType === 'privatekey' && privateKeyAccount) {
        const gasPrice = await web3.eth.getGasPrice();
        const nonce = await web3.eth.getTransactionCount(account);

        const txObject = {
          ...transactionObject,
          gasPrice: gasPrice,
          nonce: nonce,
          from: account
        };

        // Estimate gas if not provided
        if (!txObject.gas) {
          txObject.gas = await web3.eth.estimateGas(txObject);
        }

        const signedTx = await web3.eth.accounts.signTransaction(txObject, privateKeyAccount.privateKey);
        const receipt = await web3.eth.sendSignedTransaction(signedTx.rawTransaction);
        return receipt;
      } else {
        // Use MetaMask
        const receipt = await web3.eth.sendTransaction(transactionObject);
        return receipt;
      }
    } catch (err) {
      console.error('Error sending transaction:', err);
      throw err;
    }
  };

  useEffect(() => {
    // Try to restore session from stored private key
    const storedKey = sessionStorage.getItem('encryptedKey');
    if (storedKey) {
      const privateKey = atob(storedKey);
      initWeb3WithPrivateKey(privateKey);
    } else {
      // Don't auto-initialize MetaMask, just set loading to false
      // User will explicitly connect when they click the button
      setLoading(false);
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []);

  const value = {
    web3,
    account,
    contract,
    usdtContract,
    tokenContract,
    loading,
    error,
    chainId,
    isOwner,
    isAdmin,
    connectionType,
    connectWallet,
    connectWithPrivateKey,
    switchToBSCMainnet,
    disconnectWallet,
    getBalances,
    checkAndApproveToken,
    sendTransaction,
    CONTRACT_ADDRESS,
    USDT_ADDRESS: usdtAddress,
    TOKEN_ADDRESS: tokenAddress
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};